import React, { useEffect, useState } from "react";
import { Input, Modal, Pagination, Spin, Select } from "antd";
import trans_pic from "../../../assests/images/payment_image.png";
import { BiSearch, BiShow, BiUserCircle } from "react-icons/bi";
import { AiOutlineLink } from "react-icons/ai";
import { GoCopy } from "react-icons/go";
import { IoClose } from "react-icons/io5";
import { changeHeader } from "../../../redux/reducer/headerSlice";
import { useDispatch } from "react-redux";
import handlePost from "../../../utils/Post";
import handleGet from "../../../utils/Get";
import { BASE_URL } from "../../../utils/config";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiOutlineArrowDownRight, HiArrowUpLeft } from "react-icons/hi2";

const { Option } = Select;

/* ─── Badge ─────────────────────────────────────────── */
const Badge = ({ status }) => {
  const map = {
    approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    Confirmed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    confirmed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    declined: "bg-red-500/10 text-red-400 border-red-500/30",
    rejected: "bg-red-500/10 text-red-400 border-red-500/30",
    pending: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    processing: "bg-sky-500/10 text-sky-400 border-sky-500/30",
    active: "bg-sky-500/10 text-sky-400 border-sky-500/30",
  };
  const cls = map[(status || "processing").toLowerCase()] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {status || "processing"}
    </span>
  );
};

/* ─── CopyRow ────────────────────────────────────────── */
const CopyRow = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold text-[#8B8FA8] uppercase tracking-widest mb-1">{label}</p>
    <div className="flex items-center gap-2">
      <p className="text-sm font-mono text-white truncate max-w-[220px] sm:max-w-[260px]">{value ?? "N/A"}</p>
      {value && (
        <button
          onClick={() => navigator.clipboard?.writeText(value)}
          className="shrink-0 w-6 h-6 flex items-center justify-center rounded-md bg-[#161826] border border-[#2A2D3E] hover:border-[#6C5DD3]/50 text-[#8B8FA8] hover:text-white transition-colors"
        >
          <GoCopy size={12} />
        </button>
      )}
    </div>
  </div>
);

/* ─── StatusDropdown ─────────────────────────────────── */
const StatusDropdown = ({ currentStatus, transactionId, onStatusChange, loading }) => {
  const options = [
    { value: "approved", label: "Approve", color: "#34d399" },
    { value: "rejected", label: "Reject", color: "#f87171" },
  ];
  return (
    <Select
      value={currentStatus?.toLowerCase()}
      onChange={(val) => onStatusChange(transactionId, val)}
      loading={loading}
      disabled={loading}
      popupClassName="status-dropdown-popup"
      className="status-select-dark"
      style={{ minWidth: 120 }}
      dropdownStyle={{
        background: "#0D0F1A",
        border: "1px solid #1E2030",
        borderRadius: "10px",
        padding: "4px",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {options.map((opt) => (
        <Option key={opt.value} value={opt.value}>
          <span className="flex items-center gap-2">
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: opt.color, display: "inline-block", flexShrink: 0 }} />
            <span style={{ color: opt.color, fontSize: 12, fontWeight: 600 }}>{opt.label}</span>
          </span>
        </Option>
      ))}
    </Select>
  );
};

/* ══════════════════════════════════════════════════════ */
const Transaction = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [metaData, setMetaData] = useState("");
  const [isModalOpen, setIsModalOpen] = useState({ open: false, data: "" });
  const [transactions, setTransactions] = useState("");
  const [statusLoading, setStatusLoading] = useState({});
  const [userName, setUserName] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const { AdminDashboard } = t("admin", { returnObjects: true });
  const {
    TransactionDetails, filter, NoTransactionsFound,
    TransactionID, TimeStamp, From, To, Value,
    TransactionHash, Status, Note,
  } = t("UserManagment", { returnObjects: true });

  useEffect(() => { dispatch(changeHeader(AdminDashboard)); }, [AdminDashboard]);

  const fetchTransactions = async () => {
    const data = await handleGet(`${BASE_URL}transaction/get/all?page_number=${currentPage}`);
    setTransactions(data?.transactions);
    setMetaData(data?.meta);
  };

  useEffect(() => {
    if (isModalOpen?.open) {
      handleGet(`${BASE_URL}get/user/detail/${isModalOpen?.data?.user_id}`)
        .then(u => setUserName(u?.user?.email));
    }
  }, [isModalOpen]);

  useEffect(() => { fetchTransactions(); }, [currentPage]);

  const handleCancel = () => setIsModalOpen(false);

  const handleStatusChange = async (transactionId, newStatus) => {
    setStatusLoading(prev => ({ ...prev, [transactionId]: true }));
    try {
      let endpoint = "";
      if (newStatus === "approved") endpoint = `${BASE_URL}transaction/accept/${transactionId}`;
      else if (newStatus === "rejected") endpoint = `${BASE_URL}transaction/decline/${transactionId}`;
      if (endpoint) { await handlePost(endpoint, {}); fetchTransactions(); }
    } catch (e) {
      console.log(e);
    } finally {
      setStatusLoading(prev => ({ ...prev, [transactionId]: false }));
    }
  };

  const filteredTransactions = Array.isArray(transactions)
    ? transactions.filter(item => item?.amount?.toString()?.includes(searchValue))
    : [];

  const modalStyles = {
    content: { background: "#0D0F1A", border: "1px solid #1E2030", borderRadius: "16px", padding: "20px" },
    mask: { backdropFilter: "blur(4px)", background: "rgba(0,0,0,0.75)" },
  };

  return (
    <>
      <style>{`
        .status-select-dark .ant-select-selector {
          background: #0D0F1A !important;
          border: 1px solid #2A2D3E !important;
          border-radius: 8px !important;
          color: #8B8FA8 !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          height: 30px !important;
          padding: 0 10px !important;
          display: flex;
          align-items: center;
          transition: border-color 0.2s;
        }
        .status-select-dark:hover .ant-select-selector { border-color: #6C5DD3 !important; }
        .status-select-dark .ant-select-selection-item { display: flex !important; align-items: center !important; line-height: 28px !important; color: inherit !important; }
        .status-select-dark .ant-select-arrow { color: #8B8FA8 !important; }
        .status-dropdown-popup .ant-select-item { background: transparent !important; border-radius: 6px !important; margin: 2px 0 !important; }
        .status-dropdown-popup .ant-select-item:hover,
        .status-dropdown-popup .ant-select-item-option-active { background: #161826 !important; }
        .status-dropdown-popup .ant-select-item-option-selected { background: #1A1D2E !important; }
      `}</style>

      <div className="min-h-screen bg-[#080A12] p-3 sm:p-4 lg:p-6 font-sans">
        <div className="max-w-6xl mx-auto">

          {/* ── Header ── */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-white">Transactions</h2>
              <p className="text-xs text-[#8B8FA8] mt-0.5">
                {Array.isArray(transactions) ? `${transactions.length} total` : "Loading..."}
              </p>
            </div>
            <Input
              className="w-full sm:max-w-xs h-10 rounded-xl"
              placeholder="Search by amount..."
              prefix={<BiSearch size={18} className="text-[#8B8FA8]" />}
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              style={{ borderColor: "#2A2D3E", color: "#fff", borderRadius: "8px" }}
              allowClear
            />
          </div>

          {/* ── Table card ── */}
          <div className="bg-[#0D0F1A] border border-[#1E2030] rounded-2xl overflow-hidden">

            {/* Scrollable wrapper — single table for all sizes */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[580px]">

                {/* Head */}
                <thead>
                  <tr className="bg-[#080A12] border-b border-[#1E2030]">
                    {["Asset", "Amount", "Type", "Date", "Status", "View"].map((h, i) => (
                      <th
                        key={h}
                        className={`text-xs font-semibold text-[#8B8FA8] uppercase tracking-widest py-3 px-4 ${i === 5 ? "text-right" : "text-left"}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* Body */}
                <tbody>
                  {/* Loading */}
                  {!transactions && (
                    <tr>
                      <td colSpan={6}>
                        <div className="flex justify-center items-center py-20">
                          <div className="w-8 h-8 border-2 border-[#6C5DD3] border-t-transparent rounded-full animate-spin" />
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Empty */}
                  {Array.isArray(transactions) && filteredTransactions.length === 0 && (
                    <tr>
                      <td colSpan={6}>
                        <div className="flex flex-col items-center py-16 px-4">
                          <img className="max-w-[180px] w-full opacity-30 object-contain" src={trans_pic} alt="No Transactions" />
                          <p className="mt-6 text-base font-bold text-white">{NoTransactionsFound}</p>
                          <p className="text-xs text-[#8B8FA8] mt-1">{filter}</p>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Rows */}
                  {filteredTransactions.map((item, idx) => (
                    <tr
                      key={item?.id}
                      className={`hover:bg-[#161826]/50 transition-colors ${idx !== 0 ? "border-t border-[#1E2030]" : ""}`}
                    >
                      {/* Asset */}
                      <td className="px-4 py-3 sm:py-4">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-[#7b4dff] border border-[#2A2D3E] flex items-center justify-center shrink-0">
                            <img src={item?.asset?.symbol} className="w-4 h-4 sm:w-5 sm:h-5 object-contain" alt={item?.asset?.name} />
                          </div>
                          <div>
                            <p className="text-xs sm:text-sm font-bold text-white leading-tight whitespace-nowrap">{item?.asset?.name}</p>
                            <p className="text-xs text-[#8B8FA8]">{item?.asset?.short_name}</p>
                          </div>
                        </div>
                      </td>

                      {/* Amount */}
                      <td className="px-4 py-3 sm:py-4">
                        <p className="text-xs sm:text-sm font-mono text-[#6C5DD3] font-bold whitespace-nowrap">{item?.amount}</p>
                      </td>

                      {/* Type */}
                      <td className="px-4 py-3 sm:py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-mono font-semibold border whitespace-nowrap
                          ${item?.type === "withdraw" || item?.type === "withdrawal"
                            ? "bg-red-500/5 border-red-500/20 text-red-400"
                            : "bg-emerald-500/5 border-emerald-500/20 text-emerald-400"}`}>
                          {item?.type === "withdraw"
                            ? <><HiOutlineArrowDownRight size={10} /> withdrawal</>
                            : <><HiArrowUpLeft size={10} /> {item?.type}</>}
                        </span>
                      </td>

                      {/* Date */}
                      <td className="px-4 py-3 sm:py-4">
                        <p className="text-xs font-mono text-[#8B8FA8] whitespace-nowrap">
                          {item?.sent_at ? dayjs(item?.sent_at).format("DD-MM-YYYY") : dayjs().format("DD-MM-YYYY")}
                        </p>
                        <p className="text-xs font-mono text-[#3D4160]">
                          {item?.sent_at ? dayjs(item?.sent_at).format("HH:mm") : dayjs().format("HH:mm")}
                        </p>
                      </td>

                      {/* Status Dropdown */}
                      <td className="px-4 py-3 sm:py-4" onClick={e => e.stopPropagation()}>
                        {item?.status === 'confirmed' ? (
                          <Badge status="Confirmed" />
                        ) : (
                          <div className="mt-2" onClick={e => e.stopPropagation()}>
                            <StatusDropdown
                              currentStatus={item?.status}
                              transactionId={item?.id}
                              onStatusChange={handleStatusChange}
                              loading={!!statusLoading[item?.id]}
                            />
                          </div>
                        )}
                      </td>

                      {/* View */}
                      <td className="px-4 py-3 sm:py-4 text-right">
                        <button
                          onClick={() => setIsModalOpen({ open: true, data: item })}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#161826] border border-[#2A2D3E] hover:border-[#6C5DD3]/50 text-[#8B8FA8] hover:text-white transition-all"
                        >
                          <BiShow size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination footer */}
            {Array.isArray(transactions) && transactions.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 sm:px-5 py-4 border-t border-[#1E2030] bg-[#080A12]">
                <span className="text-xs text-[#8B8FA8] font-mono order-2 sm:order-1">
                  Page {currentPage} of {Math.ceil((metaData?.total || 0) / (metaData?.per_page || 10))}
                </span>
                <div className="order-1 sm:order-2">
                  <Pagination
                    current={currentPage}
                    defaultCurrent={1}
                    total={metaData?.total}
                    pageSize={metaData?.per_page}
                    onChange={page => setCurrentPage(page)}
                    showSizeChanger={false}
                    size="small"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ══ Detail Modal ══ */}
        <Modal
          centered
          open={isModalOpen?.open}
          onOk={handleCancel}
          onCancel={handleCancel}
          footer={false}
          closeIcon={false}
          styles={modalStyles}
          width="min(520px, 95vw)"
        >
          {/* Modal header */}
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-[#1E2030]">
            <h1 className="text-base sm:text-lg font-bold text-white">{TransactionDetails}</h1>
            <button
              onClick={handleCancel}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#1A1D2E] hover:bg-[#252840] text-[#8B8FA8] hover:text-white transition-colors"
            >
              <IoClose size={16} />
            </button>
          </div>

          {/* User row */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3 p-3 bg-[#080A12] border border-[#1E2030] rounded-xl">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#161826] border border-[#2A2D3E] flex items-center justify-center shrink-0">
                <BiUserCircle size={18} className="text-[#6C5DD3]" />
              </div>
              <div>
                <p className="text-xs text-[#8B8FA8] uppercase tracking-widest font-semibold">User</p>
                <p className="text-xs sm:text-sm font-mono text-white truncate max-w-[180px] sm:max-w-none">{userName || "—"}</p>
              </div>
            </div>
            <Link to={`/admin/managment/${isModalOpen?.data?.user_id}`}>
              <button className="flex items-center gap-2 px-3 py-1.5 bg-[#161826] border border-[#2A2D3E] hover:border-[#6C5DD3]/50 rounded-lg text-xs text-[#8B8FA8] hover:text-white transition-colors font-mono whitespace-nowrap">
                <AiOutlineLink size={14} /> View Profile
              </button>
            </Link>
          </div>

          {/* Details */}
          <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-0.5">

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-[#080A12] border border-[#1E2030] rounded-xl">
              <CopyRow label={TransactionID} value={isModalOpen?.data?.uuid} />
              <CopyRow label={TransactionHash} value={isModalOpen?.data?.tx_id ?? null} />
            </div>

            <div className="grid grid-cols-2 gap-3 p-4 bg-[#080A12] border border-[#1E2030] rounded-xl">
              <div>
                <p className="text-xs font-semibold text-[#8B8FA8] uppercase tracking-widest mb-1">{TimeStamp}</p>
                <p className="text-xs sm:text-sm font-mono text-white">
                  {isModalOpen?.data?.sent_at
                    ? dayjs(isModalOpen?.data?.sent_at).format("DD-MM-YYYY HH:mm:ss")
                    : dayjs().format("DD-MM-YYYY HH:mm:ss")}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#8B8FA8] uppercase tracking-widest mb-1">Block</p>
                <p className="text-xs sm:text-sm font-mono text-white">N/A</p>
              </div>
            </div>

            <div className="p-4 bg-[#080A12] border border-[#1E2030] rounded-xl space-y-3">
              <CopyRow label={From} value={isModalOpen?.data?.from_address} />
              <CopyRow label={To} value={isModalOpen?.data?.to_address} />
            </div>

            <div className="grid grid-cols-2 gap-3 p-4 bg-[#080A12] border border-[#1E2030] rounded-xl">
              <div>
                <p className="text-xs font-semibold text-[#8B8FA8] uppercase tracking-widest mb-1">{Value}</p>
                <div className="flex items-center gap-2">
                  <p className="text-xs sm:text-sm font-mono font-bold text-[#6C5DD3]">{isModalOpen?.data?.amount ?? "N/A"}</p>
                  {isModalOpen?.data?.amount && (
                    <button
                      onClick={() => navigator.clipboard?.writeText(String(isModalOpen.data.amount))}
                      className="w-5 h-5 flex items-center justify-center rounded bg-[#161826] border border-[#2A2D3E] text-[#8B8FA8] hover:text-white transition-colors"
                    >
                      <GoCopy size={10} />
                    </button>
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#8B8FA8] uppercase tracking-widest mb-1">{Status}</p>
                <Badge status={isModalOpen?.data?.status} />
              </div>
            </div>

            {isModalOpen?.data?.note && (
              <div className="p-4 bg-[#080A12] border border-[#1E2030] rounded-xl">
                <p className="text-xs font-semibold text-[#8B8FA8] uppercase tracking-widest mb-1">{Note}</p>
                <p className="text-xs sm:text-sm font-mono text-white">{isModalOpen?.data?.note}</p>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </>
  );
};

export default Transaction;